import type { TFunction } from 'i18next';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import {
  AnalyticsChartType,
  AnalyticsComponentFieldsFragment,
  AnalyticsComponentKind,
} from '@/modules/common/types';

import {
  buildAnalyticsChartData,
  extractAnalyticsTableRows,
  readAnalyticsNumber,
  readAnalyticsPresentation,
  readAnalyticsRecord,
  readAnalyticsString,
} from './analytics-chart-data';
import {
  getAnalyticsFieldDisplay,
  getAnalyticsFieldLabel,
  getAnalyticsSeriesDisplay,
  getAnalyticsSeriesLabel,
  getAnalyticsSeriesUnit,
  getAnalyticsValueDisplayLabel,
  getAnalyticsValueLabel,
  getDisplayTableColumns,
  type AnalyticsDisplayTableColumn,
} from './analytics-display';

type AnalyticsRenderableComponent =
  | AnalyticsComponentFieldsFragment
  | {
      id?: string | null;
      title: string;
      description?: string | null;
      kind: AnalyticsComponentKind;
      chartType?: AnalyticsChartType | null;
      spec: unknown;
      isStale?: boolean | null;
      resolvedValues: Array<{
        dataPointId: string;
        title: string;
        value?: unknown | null;
        isStale?: boolean | null;
      }>;
    };

const DEFAULT_COLORS = ['#2563eb', '#16a34a', '#f97316', '#7c3aed', '#0891b2', '#dc2626'];

function getChartCategoryKey(spec: unknown) {
  const chart = readAnalyticsRecord(readAnalyticsRecord(spec).chart);
  return readAnalyticsString(chart.xKey) ?? readAnalyticsString(chart.categoryKey) ?? 'name';
}

function formatValue(
  value: unknown,
  t: TFunction,
  locale?: string,
  spec?: unknown,
  fieldKey?: string | null,
  valueFormat?: string | null
) {
  if (value === null || value === undefined) {
    return '-';
  }

  if (typeof value === 'number') {
    return formatNumber(value, locale, valueFormat);
  }

  if (typeof value === 'string') {
    const displayLabel = getAnalyticsValueDisplayLabel(spec, value, fieldKey);
    if (displayLabel) {
      return displayLabel;
    }

    const numericValue = readAnalyticsNumber(value);
    if (numericValue !== null) {
      return formatNumber(numericValue, locale, valueFormat);
    }

    return value;
  }

  if (typeof value === 'boolean') {
    const displayLabel = getAnalyticsValueDisplayLabel(spec, value, fieldKey);
    if (displayLabel) {
      return displayLabel;
    }

    return value
      ? t('modules.mavue2.analytics.values.yes')
      : t('modules.mavue2.analytics.values.no');
  }

  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

function formatNumber(value: number, locale?: string, valueFormat?: string | null) {
  if (valueFormat === 'integer') {
    return new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(value);
  }

  if (valueFormat === 'percent') {
    return new Intl.NumberFormat(locale, {
      style: 'percent',
      maximumFractionDigits: 2,
    }).format(value);
  }

  const currency = valueFormat?.startsWith('currency:')
    ? valueFormat.slice('currency:'.length).trim().toUpperCase()
    : null;
  if (currency) {
    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        maximumFractionDigits: 2,
      }).format(value);
    } catch {
      return new Intl.NumberFormat(locale, { maximumFractionDigits: 2 }).format(value);
    }
  }

  return new Intl.NumberFormat(locale, { maximumFractionDigits: 2 }).format(value);
}

function withUnit(value: string, unit?: string | null) {
  return unit ? `${value} ${unit}` : value;
}

function getFieldValueFormat(
  spec: unknown,
  key: string,
  column?: AnalyticsDisplayTableColumn | null
) {
  const fieldDisplay = getAnalyticsFieldDisplay(spec, key);
  return column?.valueFormat ?? column?.format ?? fieldDisplay?.valueFormat ?? fieldDisplay?.format;
}

function getSeriesValueFormat(spec: unknown, key: string) {
  const seriesDisplay = getAnalyticsSeriesDisplay(spec, key);
  return seriesDisplay?.valueFormat ?? seriesDisplay?.format;
}

function formatTableColumnLabel(spec: unknown, column: AnalyticsDisplayTableColumn) {
  const fieldDisplay = getAnalyticsFieldDisplay(spec, column.key);
  const label = column.label ?? getAnalyticsFieldLabel(spec, column.key);
  const unit = column.unit ?? fieldDisplay?.unit;

  return unit ? `${label} (${unit})` : label;
}

function ChartContent({ component }: { component: AnalyticsRenderableComponent }) {
  const { t, i18n } = useTranslation();
  const { chartType, data, isStacked, seriesKeys } = buildAnalyticsChartData(component);
  const presentation = readAnalyticsPresentation(component.spec);
  const categoryKey = getChartCategoryKey(component.spec);
  const showLegend = presentation.legend !== false && (seriesKeys.length > 1 || data.length > 1);
  const isPieLike = chartType === AnalyticsChartType.PIE || chartType === AnalyticsChartType.DONUT;
  const formatTooltipValue = (value: unknown, name: unknown) => {
    const seriesKey = isPieLike ? (seriesKeys[0] ?? 'value') : String(name ?? '');
    const formattedValue = formatValue(
      value,
      t,
      i18n.language,
      component.spec,
      seriesKey,
      getSeriesValueFormat(component.spec, seriesKey)
    );
    const label = isPieLike
      ? getAnalyticsValueLabel(component.spec, name, categoryKey)
      : getAnalyticsSeriesLabel(component.spec, seriesKey);

    return [withUnit(formattedValue, getAnalyticsSeriesUnit(component.spec, seriesKey)), label];
  };
  const formatTooltipLabel = (label: unknown) =>
    getAnalyticsValueLabel(component.spec, label, categoryKey);
  const formatLegendValue = (value: unknown) =>
    isPieLike
      ? getAnalyticsValueLabel(component.spec, value, categoryKey)
      : getAnalyticsSeriesLabel(component.spec, String(value ?? ''));

  if (data.length === 0 || seriesKeys.length === 0) {
    return (
      <div className="text-sm text-muted-foreground">
        {t('modules.mavue2.analytics.chart.noNumericData')}
      </div>
    );
  }

  if (chartType === AnalyticsChartType.PIE || chartType === AnalyticsChartType.DONUT) {
    const valueKey = seriesKeys[0];

    return (
      <div className="h-72 min-w-[360px]">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Tooltip formatter={formatTooltipValue} labelFormatter={formatTooltipLabel} />
            {showLegend ? <Legend formatter={formatLegendValue} /> : null}
            <Pie
              data={data}
              dataKey={valueKey}
              nameKey="name"
              innerRadius={chartType === AnalyticsChartType.DONUT ? 58 : 0}
              outerRadius={92}
              paddingAngle={2}
            >
              {data.map((entry, index) => (
                <Cell
                  key={`${entry.name}-${index}`}
                  fill={DEFAULT_COLORS[index % DEFAULT_COLORS.length]}
                />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
      </div>
    );
  }

  if (chartType === AnalyticsChartType.LINE) {
    return (
      <div className="h-72 min-w-[360px]">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="name"
              tickLine={false}
              axisLine={false}
              tickFormatter={(value) => getAnalyticsValueLabel(component.spec, value, categoryKey)}
            />
            <YAxis tickLine={false} axisLine={false} width={48} />
            <Tooltip formatter={formatTooltipValue} labelFormatter={formatTooltipLabel} />
            {showLegend ? <Legend formatter={formatLegendValue} /> : null}
            {seriesKeys.map((seriesKey, index) => (
              <Line
                key={seriesKey}
                type="monotone"
                dataKey={seriesKey}
                name={seriesKey}
                stroke={DEFAULT_COLORS[index % DEFAULT_COLORS.length]}
                strokeWidth={2}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    );
  }

  if (chartType === AnalyticsChartType.AREA) {
    return (
      <div className="h-72 min-w-[360px]">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="name"
              tickLine={false}
              axisLine={false}
              tickFormatter={(value) => getAnalyticsValueLabel(component.spec, value, categoryKey)}
            />
            <YAxis tickLine={false} axisLine={false} width={48} />
            <Tooltip formatter={formatTooltipValue} labelFormatter={formatTooltipLabel} />
            {showLegend ? <Legend formatter={formatLegendValue} /> : null}
            {seriesKeys.map((seriesKey, index) => (
              <Area
                key={seriesKey}
                type="monotone"
                dataKey={seriesKey}
                name={seriesKey}
                stroke={DEFAULT_COLORS[index % DEFAULT_COLORS.length]}
                fill={DEFAULT_COLORS[index % DEFAULT_COLORS.length]}
                fillOpacity={0.18}
              />
            ))}
          </AreaChart>
        </ResponsiveContainer>
      </div>
    );
  }

  return (
    <div className="h-72 min-w-[360px]">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis
            dataKey="name"
            tickLine={false}
            axisLine={false}
            tickFormatter={(value) => getAnalyticsValueLabel(component.spec, value, categoryKey)}
          />
          <YAxis tickLine={false} axisLine={false} width={48} />
          <Tooltip formatter={formatTooltipValue} labelFormatter={formatTooltipLabel} />
          {showLegend ? <Legend formatter={formatLegendValue} /> : null}
          {seriesKeys.map((seriesKey, index) => (
            <Bar
              key={seriesKey}
              dataKey={seriesKey}
              name={seriesKey}
              fill={DEFAULT_COLORS[index % DEFAULT_COLORS.length]}
              radius={isStacked ? undefined : [4, 4, 0, 0]}
              stackId={isStacked ? 'analytics' : undefined}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function KpiContent({ component }: { component: AnalyticsRenderableComponent }) {
  const { t, i18n } = useTranslation();
  const firstValue = component.resolvedValues[0];
  const presentation = readAnalyticsPresentation(component.spec);
  const unit =
    readAnalyticsString(presentation.unit) ?? getAnalyticsSeriesUnit(component.spec, 'value');
  const numericValue = readAnalyticsNumber(firstValue?.value);

  return (
    <div className="space-y-2">
      <div className="text-3xl font-semibold tracking-normal">
        {numericValue === null
          ? formatValue(
              firstValue?.value,
              t,
              i18n.language,
              component.spec,
              'value',
              getSeriesValueFormat(component.spec, 'value')
            )
          : formatValue(
              numericValue,
              t,
              i18n.language,
              component.spec,
              'value',
              getSeriesValueFormat(component.spec, 'value')
            )}
        {unit ? (
          <span className="ml-1 text-base font-medium text-muted-foreground">{unit}</span>
        ) : null}
      </div>
      {firstValue ? <div className="text-sm text-muted-foreground">{firstValue.title}</div> : null}
    </div>
  );
}

function TableContent({ component }: { component: AnalyticsRenderableComponent }) {
  const { t, i18n } = useTranslation();
  const tableGroups = component.resolvedValues
    .map((resolvedValue) => ({
      resolvedValue,
      rows: extractAnalyticsTableRows(resolvedValue.value),
    }))
    .filter((group) => group.rows.length > 0);

  if (tableGroups.length > 0) {
    return (
      <div className="space-y-4">
        {tableGroups.map(({ resolvedValue, rows }) => {
          const rowValues = rows.map(({ row }) => row);
          const columns = getDisplayTableColumns(component.spec, rowValues);

          if (columns.length === 0) {
            return null;
          }

          return (
            <div key={resolvedValue.dataPointId}>
              <div className="overflow-x-auto rounded-md border">
                <table className="min-w-full text-sm">
                  <thead className="bg-muted/50">
                    <tr>
                      {columns.map((column) => (
                        <th
                          key={column.key}
                          className="whitespace-nowrap px-3 py-2 text-left font-medium"
                        >
                          {formatTableColumnLabel(component.spec, column)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map(({ row, fallbackName }, rowIndex) => (
                      <tr key={fallbackName || rowIndex} className="border-t">
                        {columns.map((column) => (
                          <td key={column.key} className="whitespace-nowrap px-3 py-2 align-top">
                            {formatValue(
                              row[column.key],
                              t,
                              i18n.language,
                              component.spec,
                              column.key,
                              getFieldValueFormat(component.spec, column.key, column)
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className="overflow-x-auto rounded-md border">
      <table className="min-w-full table-fixed text-sm">
        <thead className="bg-muted/50">
          <tr>
            <th className="px-3 py-2 text-left font-medium">
              {t('modules.mavue2.analytics.chart.table.metric')}
            </th>
            <th className="px-3 py-2 text-left font-medium">
              {t('modules.mavue2.analytics.chart.table.value')}
            </th>
          </tr>
        </thead>
        <tbody>
          {component.resolvedValues.map((resolvedValue) => (
            <tr key={resolvedValue.dataPointId} className="border-t">
              <td className="px-3 py-2 align-top">{resolvedValue.title}</td>
              <td className="break-words px-3 py-2 align-top">
                {formatValue(resolvedValue.value, t, i18n.language, component.spec)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function InsightContent({ component }: { component: AnalyticsRenderableComponent }) {
  const { t } = useTranslation();
  const spec = readAnalyticsRecord(component.spec);
  const text =
    readAnalyticsString(spec.text) ??
    component.description ??
    t('modules.mavue2.analytics.chart.emptyInsight');

  return <div className="whitespace-pre-wrap text-sm leading-6 text-foreground">{text}</div>;
}

export function AnalyticsChartRenderer({
  component,
  compact = false,
  headerActions = null,
}: {
  component: AnalyticsRenderableComponent;
  compact?: boolean;
  headerActions?: ReactNode;
}) {
  const { t } = useTranslation();

  return (
    <Card className="flex h-full min-h-0 flex-col shadow-none">
      <CardHeader className={cn('shrink-0', compact ? 'space-y-1 p-4' : 'space-y-2')}>
        <div className="flex min-w-0 items-start justify-between gap-3">
          <CardTitle className={compact ? 'text-base' : 'text-lg'}>{component.title}</CardTitle>
          {component.isStale ? (
            <Badge
              variant="outline"
              className="shrink-0 border-amber-200 bg-amber-50 text-amber-800"
            >
              {t('modules.mavue2.analytics.status.stale')}
            </Badge>
          ) : null}
          {headerActions}
        </div>
        {component.description ? (
          <div className="text-sm text-muted-foreground">{component.description}</div>
        ) : null}
      </CardHeader>
      <CardContent className={cn('min-h-0 flex-1 overflow-auto', compact && 'p-4 pt-0')}>
        {component.kind === AnalyticsComponentKind.KPI_CARD ? (
          <KpiContent component={component} />
        ) : component.kind === AnalyticsComponentKind.DATA_TABLE ? (
          <TableContent component={component} />
        ) : component.kind === AnalyticsComponentKind.INSIGHT_TEXT ? (
          <InsightContent component={component} />
        ) : (
          <ChartContent component={component} />
        )}
      </CardContent>
    </Card>
  );
}
